import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Fire, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import TrackedLinks from "@/components/TrackedLinks";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const fmt = (d) => (d ? new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-line bg-canvas p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-stone">{label}</div>
      <div className="font-serif text-3xl text-ink mt-2">{value}</div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-2xl border border-line bg-canvas p-5" data-testid={testid}>
      <div className="font-serif text-lg text-ink mb-3">{title}</div>
      {children}
    </div>
  );
}

function BarList({ rows, k, v, testid }) {
  const max = Math.max(1, ...rows.map((r) => r[v]));
  if (!rows.length) return <div className="text-sm text-stone">Nessun dato ancora</div>;
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r) => (
        <div key={r[k]} className="text-sm">
          <div className="flex justify-between text-ink"><span className="truncate pr-2">{r[k]}</span><span className="text-stone">{r[v]}</span></div>
          <div className="h-1.5 rounded-full bg-sand mt-1"><div className="h-1.5 rounded-full bg-olive transition-[width] duration-700" style={{ width: `${(r[v] / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function MiniBars({ data, testid }) {
  return (
    <div className="h-40" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}><XAxis dataKey="l" tick={{ fontSize: 10 }} interval={0} /><Tooltip /><Bar dataKey="n" fill="#36453B" radius={[4, 4, 0, 0]} /></BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function VisitorRow({ v, i, prefix }) {
  const copy = async () => { try { await navigator.clipboard.writeText(v.email); toast.success("Email copiata"); } catch { toast.error("Copia non riuscita"); } };
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 py-3 border-b border-line last:border-0" data-testid={`${prefix}-${i}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {v.high_intent && <Fire size={16} weight="fill" className="text-terracotta" />}
          <span className="font-medium text-ink">{v.name || `Visitatore anonimo · ${v.city || "?"}`}</span>
          {v.kind && <Badge variant="outline">{v.kind}{v.status ? ` · ${v.status}` : ""}</Badge>}
        </div>
        <div className="text-xs text-stone mt-1">
          {v.pageviews} pagine · {v.sessions} visite · {v.sources.join(", ")} · {v.device} · {v.city || ""} · ultima: {fmt(v.last_seen)}
        </div>
        <div className="text-xs text-stone/80 mt-1 truncate">{v.top_pages.join("  ·  ")}</div>
      </div>
      {v.email && <Button size="sm" variant="outline" className="rounded-full" onClick={copy} data-testid={`${prefix}-copy-${i}`}><Copy size={14} className="mr-1" />{v.email}</Button>}
    </div>
  );
}

function GoalBar({ label, cur, goal, testid }) {
  const pct = Math.min(100, Math.round((cur / goal) * 100));
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-sm"><span className="text-ink">{label}</span><span className="text-stone"><b className="text-ink">{cur}</b> / {goal} · {pct}%</span></div>
      <div className="h-2.5 rounded-full bg-sand mt-1.5 overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-700 ${pct >= 100 ? "bg-olive" : "bg-terracotta"}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function WeeklyGoals() {
  const [g, setG] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/visits/goals").then((r) => setG(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    await api.put("/admin/visits/goals", { visits: Number(edit.visits), signups: Number(edit.signups) });
    setEdit(null); load(); toast.success("Obiettivi aggiornati");
  };
  if (!g) return null;
  return (
    <Card title="Obiettivi della settimana (da lunedì)" testid="visits-goals">
      <div className="grid md:grid-cols-2 gap-6">
        <GoalBar label="Visite" cur={g.progress.visits} goal={g.goals.visits} testid="goal-visits" />
        <GoalBar label="Registrazioni" cur={g.progress.signups} goal={g.goals.signups} testid="goal-signups" />
      </div>
      {edit ? (
        <div className="flex flex-wrap gap-2 mt-4 items-center">
          <input type="number" min="1" className="w-28 rounded-lg border border-line px-2 py-1 text-sm bg-canvas" value={edit.visits} onChange={(e) => setEdit({ ...edit, visits: e.target.value })} data-testid="goal-visits-input" />
          <input type="number" min="1" className="w-28 rounded-lg border border-line px-2 py-1 text-sm bg-canvas" value={edit.signups} onChange={(e) => setEdit({ ...edit, signups: e.target.value })} data-testid="goal-signups-input" />
          <Button size="sm" className="rounded-full" onClick={save} data-testid="goal-save-btn">Salva</Button>
        </div>
      ) : <button className="text-xs underline text-stone mt-3" onClick={() => setEdit(g.goals)} data-testid="goal-edit-btn">Modifica obiettivi</button>}
    </Card>
  );
}

export default function VisitsAnalytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/admin/visits?days=${days}`).then((r) => setD(r.data)).catch(() => toast.error("Errore caricamento visite")); }, [days]);

  return (
    <div className="space-y-6" data-testid="visits-analytics">
      <div className="flex gap-2">
        {[7, 30, 90].map((n) => (
          <Button key={n} size="sm" variant={days === n ? "default" : "outline"} className="rounded-full" onClick={() => setDays(n)} data-testid={`visits-days-${n}`}>{n} giorni</Button>
        ))}
      </div>
      {!d ? <div className="text-stone" data-testid="visits-loading">Caricamento…</div> : (
        <>
          <WeeklyGoals />
          <Card title="Classifica storie Instagram (visite dal link adesivo/bio)" testid="visits-top-stories">
            {d.top_stories?.length ? d.top_stories.map((s, i) => (
              <div key={s.story_id} className="flex justify-between gap-3 py-2 border-b border-line last:border-0 text-sm" data-testid={`visits-top-story-${i}`}>
                <span className="text-ink truncate"><b className="text-stone mr-2">#{i + 1}</b>{s.label} <span className="text-stone">· {s.date}{s.kind === "question" ? " · domanda" : ""}</span></span>
                <span className="whitespace-nowrap text-ink"><b>{s.visits}</b> visite · <b>{s.signups}</b> registr.</span>
              </div>
            )) : <div className="text-sm text-stone">Ancora nessuna visita dalle storie.</div>}
          </Card>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Visitatori unici" value={d.unique_visitors} testid="visits-unique" />
            <Stat label="Visite (sessioni)" value={d.sessions} testid="visits-sessions" />
            <Stat label="Pagine viste" value={d.pageviews} testid="visits-pageviews" />
            <Stat label="Identificati" value={d.identified.length} testid="visits-identified-count" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card title="Da dove arrivano" testid="visits-sources"><BarList rows={d.by_source} k="source" v="sessions" testid="visits-sources-list" /></Card>
            <Card title="Post, email e campagne" testid="visits-campaigns">
              <BarList rows={d.campaigns.map((c) => ({ ...c, key: `${c.source} · ${c.label}` }))} k="key" v="sessions" testid="visits-campaigns-list" />
            </Card>
            <Card title="Orari (ora italiana)" testid="visits-hours"><MiniBars data={d.by_hour.map((n, h) => ({ l: h, n }))} testid="visits-hours-chart" /></Card>
            <Card title="Giorni della settimana" testid="visits-weekdays"><MiniBars data={d.by_weekday.map((n, i) => ({ l: WD[i], n }))} testid="visits-weekdays-chart" /></Card>
            <Card title="Pagine più viste" testid="visits-pages"><BarList rows={d.top_pages} k="path" v="pageviews" testid="visits-pages-list" /></Card>
            <Card title="Città" testid="visits-cities"><BarList rows={d.top_cities} k="city" v="visitors" testid="visits-cities-list" /></Card>
          </div>
          <TrackedLinks />
          <Card title="Classifica post social (visite e registrazioni) — l'Autopilot ripete i 5 temi migliori" testid="visits-top-posts">
            {d.top_posts.length ? d.top_posts.map((p, i) => (
              <div key={p.post_id} className="flex justify-between gap-3 py-2 border-b border-line last:border-0 text-sm" data-testid={`visits-top-post-${i}`}>
                <span className="text-ink truncate"><b className="text-stone mr-2">#{i + 1}</b>{p.label} <span className="text-stone">· {p.platform}</span></span>
                <span className="whitespace-nowrap text-ink"><b>{p.visits}</b> visite · <b>{p.signups}</b> registr.</span>
              </div>
            )) : <div className="text-sm text-stone">Ancora nessuna visita dai post: si popolerà con i prossimi post Facebook.</div>}
          </Card>
          <Card title="Visitatori identificati (prospect e utenti) — da ricontattare" testid="visits-identified">
            {d.identified.length ? d.identified.map((v, i) => <VisitorRow key={v.visitor_id} v={v} i={i} prefix="visits-identified-row" />)
              : <div className="text-sm text-stone">Nessuno ancora. Appaiono qui quando un prospect clicca un link nelle tue email.</div>}
          </Card>
          <Card title="Visitatori anonimi caldi (3+ pagine, ritorni o pagine prezzi)" testid="visits-hot">
            {d.hot_anonymous.length ? d.hot_anonymous.map((v, i) => <VisitorRow key={v.visitor_id} v={v} i={i} prefix="visits-hot-row" />)
              : <div className="text-sm text-stone">Nessun visitatore caldo nel periodo.</div>}
          </Card>
        </>
      )}
    </div>
  );
}
